import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TableSortLabel
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Person as PersonIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterIcon,
  FilterAltOff as FilterOffIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

function PartnerReport({ data }) {
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: dayjs().subtract(6, 'month'),
    end: dayjs()
  });

  // Filter and deduplicate meetings based on selected partner and date range
  const getFilteredMeetings = useCallback((partner) => {
    // First deduplicate by Meeting ID
    const meetingMap = new Map();
    
    data.forEach(meeting => {
      const meetingId = meeting['Meeting ID'];
      if (
        meeting['Partner Name'] === partner &&
        dayjs(meeting['Meeting Date']).isAfter(dateRange.start) &&
        dayjs(meeting['Meeting Date']).isBefore(dateRange.end)
      ) {
        // If this meeting ID isn't in our map yet, or if this entry is more recent
        if (!meetingMap.has(meetingId) || 
            new Date(meeting['Timestamp']) > new Date(meetingMap.get(meetingId)['Timestamp'])) {
          meetingMap.set(meetingId, meeting);
        }
      }
    });
    
    return Array.from(meetingMap.values());
  }, [data, dateRange]);

  // Update filtered meetings when partner changes
  useEffect(() => {
    if (selectedPartner) {
      const meetings = getFilteredMeetings(selectedPartner);
      setFilteredMeetings(meetings);
    } else {
      setFilteredMeetings([]);
    }
  }, [selectedPartner, getFilteredMeetings]);

  // Filter data by date range
  const dateFilteredData = useMemo(() => {
    return data.filter(meeting => {
      const meetingDate = dayjs(meeting['Meeting Date']);
      return meetingDate.isAfter(dateRange.start) && meetingDate.isBefore(dateRange.end);
    });
  }, [data, dateRange]);

  // Calculate dashboard metrics
  const dashboardMetrics = useMemo(() => {
    // First deduplicate meetings by Meeting ID
    const uniqueMeetings = new Map();
    dateFilteredData.forEach(meeting => {
      const meetingId = meeting['Meeting ID'];
      // Only keep the most recent entry for each Meeting ID
      if (!uniqueMeetings.has(meetingId) || 
          new Date(meeting['Timestamp']) > new Date(uniqueMeetings.get(meetingId)['Timestamp'])) {
        uniqueMeetings.set(meetingId, meeting);
      }
    });

    // Now calculate partner meetings using deduplicated data
    const partnerMeetings = new Map();
    Array.from(uniqueMeetings.values()).forEach(meeting => {
      const partner = meeting['Partner Name'];
      if (partner && partner !== 'N/A') {
        partnerMeetings.set(partner, (partnerMeetings.get(partner) || 0) + 1);
      }
    });

    const pieData = Array.from(partnerMeetings.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const totalMeetings = uniqueMeetings.size;
    const totalPartners = partnerMeetings.size;
    const avgMeetingsPerPartner = totalPartners ? (totalMeetings / totalPartners).toFixed(1) : 0;

    return {
      pieData,
      totalMeetings,
      totalPartners,
      avgMeetingsPerPartner
    };
  }, [dateFilteredData]);

  // Get unique partner names
  const partners = Array.from(new Set(data.map(meeting => meeting['Partner Name'])))
    .filter(partner => partner && partner !== 'N/A')
    .sort();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#A4DE6C'];

  const getStats = () => {
    if (!selectedPartner) return null;

    const meetings = filteredMeetings;
    
    // Get unique external email addresses with meeting counts
    const contactStats = new Map(); // Map to store email -> {count, meetings}
    meetings.forEach(meeting => {
      const attendeeFields = ['Attendees', 'Meeting Attendees', 'External Attendees', 'attendees'];
      let attendees = '';
      
      for (const field of attendeeFields) {
        if (meeting[field]) {
          attendees = meeting[field];
          break;
        }
      }
      
      if (attendees) {
        const emailList = attendees.split(',')
          .map(email => email.trim())
          .filter(email => email && !email.endsWith('@codeium.com'));
        
        emailList.forEach(email => {
          if (!contactStats.has(email)) {
            contactStats.set(email, {
              count: 1,
              meetings: [meeting]
            });
          } else {
            const stats = contactStats.get(email);
            stats.count += 1;
            stats.meetings.push(meeting);
            contactStats.set(email, stats);
          }
        });
      }
    });

    // Convert contact stats to sorted array
    const sortedContacts = Array.from(contactStats.entries())
      .sort((a, b) => b[1].count - a[1].count) // Sort by meeting count descending
      .map(([email, stats]) => ({
        email,
        meetingCount: stats.count,
        meetings: stats.meetings
      }));

    return {
      totalMeetings: meetings.length,
      pioInterlocks: meetings.filter(m => m['Meeting Type'] === 'PIO Interlock').length,
      paoInterlocks: meetings.filter(m => m['Meeting Type'] === 'PAO Interlock').length,
      meetingTypes: Array.from(new Set(meetings.map(m => m['Meeting Type']))),
      interlockAccounts: Array.from(new Set(meetings.map(m => m['Interlock Account']).filter(Boolean))),
      contacts: sortedContacts
    };
  };

  const handlePartnerSelect = (event, newValue) => {
    setSelectedPartner(newValue);
    setSelectedContact(null);
  };

  const handleContactClick = (contact) => {
    if (selectedContact === contact.email) {
      // If clicking the same contact, clear the filter
      setSelectedContact(null);
      setFilteredMeetings(getFilteredMeetings(selectedPartner));
    } else {
      // Filter meetings to show only those with this contact
      setSelectedContact(contact.email);
      setFilteredMeetings(contact.meetings);
    }
  };

  const getAttendeesList = (meeting) => {
    const attendeeFields = ['Attendees', 'Meeting Attendees', 'External Attendees', 'attendees'];
    for (const field of attendeeFields) {
      if (meeting[field]) {
        return meeting[field].split(',').map(email => email.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const [orderBy, setOrderBy] = useState('Meeting Date');
  const [order, setOrder] = useState('desc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const stats = getStats();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Partner Activity Dashboard
      </Typography>

      {/* Dashboard Overview */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            {/* Date Range and Stats Section */}
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                {/* Date Range Selector */}
                <Box>
                  <Typography variant="h6" gutterBottom>Date Range</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={2}>
                      <DatePicker
                        label="Start Date"
                        value={dateRange.start}
                        onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      <DatePicker
                        label="End Date"
                        value={dateRange.end}
                        onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Box>

                {/* Summary Stats */}
                <Box>
                  <Typography variant="h6" gutterBottom>Summary</Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Total Meetings</Typography>
                      <Typography variant="h4">{dashboardMetrics.totalMeetings}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Total Partners</Typography>
                      <Typography variant="h4">{dashboardMetrics.totalPartners}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Avg Meetings per Partner</Typography>
                      <Typography variant="h4">{dashboardMetrics.avgMeetingsPerPartner}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            {/* Partner Distribution Chart */}
            <Grid item xs={12} md={8}>
              <Box>
                <Typography variant="h6" gutterBottom align="center">
                  Partner Meeting Distribution
                </Typography>
                <Box sx={{ width: '100%', height: 400, mt: 2 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={dashboardMetrics.pieData.slice(0, 7)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        fill="#8884d8"
                        label={({ name, value, percent }) => 
                          `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                        }
                        labelLine={true}
                      >
                        {dashboardMetrics.pieData.slice(0, 7).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value, name) => [`${value} meetings`, name]}
                      />
                      <Legend 
                        layout="vertical" 
                        align="right"
                        verticalAlign="middle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Partner Report Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Partner Details
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Autocomplete
            value={selectedPartner}
            onChange={handlePartnerSelect}
            options={partners}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Partner"
                variant="outlined"
                sx={{ minWidth: 300 }}
              />
            )}
            sx={{ flexGrow: 1 }}
          />
          {selectedContact && (
            <Button
              variant="outlined"
              startIcon={<FilterOffIcon />}
              onClick={() => {
                setSelectedContact(null);
                setFilteredMeetings(getFilteredMeetings(selectedPartner));
              }}
            >
              Clear Filter
            </Button>
          )}
        </Box>

        {stats && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Partner Statistics</Typography>
              <Typography>Total Meetings: {stats.totalMeetings}</Typography>
              <Typography>PIO Interlocks: {stats.pioInterlocks}</Typography>
              <Typography>PAO Interlocks: {stats.paoInterlocks}</Typography>
              <Typography>Meeting Types: {stats.meetingTypes.join(', ')}</Typography>
              <Typography>Interlock Accounts: {stats.interlockAccounts.join(', ') || 'None'}</Typography>
            </Box>

            {stats.contacts && stats.contacts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  External Contacts ({stats.contacts.length})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {stats.contacts.map((contact) => (
                    <Tooltip 
                      key={contact.email}
                      title={`Click to ${selectedContact === contact.email ? 'show all meetings' : 'filter by contact'}`}
                      arrow
                    >
                      <Chip
                        icon={<EmailIcon />}
                        label={`${contact.email} (${contact.meetingCount})`}
                        variant="outlined"
                        onClick={() => handleContactClick(contact)}
                        sx={{ 
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: selectedContact === contact.email ? 'rgba(25, 118, 210, 0.1)' :
                                         contact.meetingCount > 5 ? 'rgba(0, 200, 0, 0.1)' : 
                                         contact.meetingCount > 2 ? 'rgba(0, 150, 0, 0.05)' : 
                                         'transparent'
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}

            {filteredMeetings.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Meeting History 
                  {selectedContact && ` (Filtered by ${selectedContact})`}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'Meeting Date'}
                            direction={orderBy === 'Meeting Date' ? order : 'asc'}
                            onClick={() => handleSort('Meeting Date')}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'Title'}
                            direction={orderBy === 'Title' ? order : 'asc'}
                            onClick={() => handleSort('Title')}
                          >
                            Title
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'Meeting Type'}
                            direction={orderBy === 'Meeting Type' ? order : 'asc'}
                            onClick={() => handleSort('Meeting Type')}
                          >
                            Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'Attendees'}
                            direction={orderBy === 'Attendees' ? order : 'asc'}
                            onClick={() => handleSort('Attendees')}
                          >
                            Attendees
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'Interlock Account'}
                            direction={orderBy === 'Interlock Account' ? order : 'asc'}
                            onClick={() => handleSort('Interlock Account')}
                          >
                            Interlock Account
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMeetings
                        .sort((a, b) => {
                          // Handle date sorting
                          if (orderBy === 'Meeting Date') {
                            return order === 'asc'
                              ? new Date(a[orderBy]) - new Date(b[orderBy])
                              : new Date(b[orderBy]) - new Date(a[orderBy]);
                          }
                          // Handle attendees sorting by count
                          if (orderBy === 'Attendees') {
                            const aCount = getAttendeesList(a).length;
                            const bCount = getAttendeesList(b).length;
                            return order === 'asc'
                              ? aCount - bCount
                              : bCount - aCount;
                          }
                          // Handle string sorting
                          const aValue = (a[orderBy] || '').toString().toLowerCase();
                          const bValue = (b[orderBy] || '').toString().toLowerCase();
                          return order === 'asc'
                            ? aValue.localeCompare(bValue)
                            : bValue.localeCompare(aValue);
                        })
                        .map((meeting) => (
                          <TableRow
                            key={meeting.id}
                            hover
                            onClick={() => setSelectedMeeting(meeting)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <TableCell>{new Date(meeting['Meeting Date']).toLocaleDateString()}</TableCell>
                            <TableCell>{meeting.Title}</TableCell>
                            <TableCell>{meeting['Meeting Type']}</TableCell>
                            <TableCell>
                              {getAttendeesList(meeting).length} attendees
                              <IconButton size="small" sx={{ ml: 1 }}>
                                <ExpandMoreIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell>{meeting['Interlock Account'] || '-'}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Meeting Details Dialog */}
      <Dialog 
        open={!!selectedMeeting} 
        onClose={() => setSelectedMeeting(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMeeting && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Meeting Details</Typography>
                <IconButton onClick={() => setSelectedMeeting(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" gutterBottom>
                {selectedMeeting.Title}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                {new Date(selectedMeeting['Meeting Date']).toLocaleDateString()}
              </Typography>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Attendees
              </Typography>
              <List dense>
                {getAttendeesList(selectedMeeting).map((attendee, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={attendee}
                      secondary={attendee.endsWith('@codeium.com') ? 'Internal' : 'External'}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedMeeting(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default PartnerReport;
